// src/utils/swingUtils.js - Utilities for swing timing calculations

export class SwingUtils {
  /**
   * Helper function to analyze note type frequency
   * @param {Array} noteAnalysis - Array of analyzed notes
   * @returns {Object} - Object with note type frequencies
   */
  static getNoteTypeFrequency(noteAnalysis) {
    const frequencies = {};
    noteAnalysis.forEach(note => {
      frequencies[note.noteType] = (frequencies[note.noteType] || 0) + 1;
    });
    return frequencies;
  }
  /**
   * Apply swing timing to note events using swing grid approach
   * 
   * Swing Rules:
   * 1) All notes starting on .5 beats move to 5/8 beat position  
   * 2) All notes conform durations to swing grid:
   *    - On-beat start: duration gets +5/8 adjustment
   *    - Off-beat start: duration gets +3/8 adjustment
   * 3) Skip triplet patterns entirely
   * 
   * @param {Array} noteEvents - Array of note events with time and duration
   * @param {number} beatsPerMeasure - Number of beats per measure (default: 4)
   * @param {number} beatDuration - Duration of one beat in seconds
   * @param {number} swingRatio - Swing ratio (default: 2/3 for classic swing, now 5/8)
   * @param {boolean} debugMode - Enable debug logging
   * @returns {Array} - Array of note events with swing timing applied
   */
  static applySwingTiming(noteEvents, beatsPerMeasure = 4, beatDuration = 0.5, swingRatio = 5/8, debugMode = false) {
    if (!noteEvents || noteEvents.length === 0) {
      // console.log('SwingUtils: No note events to process');
      return [];
    }

    // console.log(`SwingUtils: Processing ${noteEvents.length} note events for swing timing`);
    // console.log(`SwingUtils: Using ${beatsPerMeasure} beats per measure, ${beatDuration.toFixed(3)}s beat duration`);
    
    // Debug output removed for cleaner console
    
    let modifiedCount = 0;
    const results = noteEvents.map((event, index) => {
      const swungEvent = { ...event };
      let wasModified = false;
      
      // Get event timing properties
      const eventTime = event.time !== undefined ? event.time : event.startTime;
      const eventDuration = event.duration !== undefined ? event.duration : 
                           (event.endTime && event.startTime ? event.endTime - event.startTime : 0);
      
      if (eventTime === undefined || isNaN(eventTime) || eventDuration === undefined) {
        return swungEvent; // Skip invalid events
      }
      
      // Calculate beat position
      const totalBeats = eventTime / beatDuration;
      const beatNumber = Math.floor(totalBeats);
      const beatFraction = totalBeats - beatNumber;
      
      // Check if this is a triplet (skip triplets)
      // Simple heuristic: if duration is approximately 1/3 of a beat, likely a triplet
      const durationInBeats = eventDuration / beatDuration;
      const isTriplet = Math.abs(durationInBeats - 1/3) < 0.05 || 
                        Math.abs(durationInBeats - 2/3) < 0.05;
      
      if (isTriplet) {
        // Skip triplets silently
        return swungEvent; // Skip triplets
      }
      
      // Rule 1: Move notes starting on .5 beats to 5/8 position
      const startsOnHalfBeat = Math.abs(beatFraction - 0.5) < 0.05;
      if (startsOnHalfBeat) {
        const newTime = beatNumber * beatDuration + (5/8) * beatDuration;
        
        if (event.time !== undefined) {
          swungEvent.time = newTime;
        } else if (event.startTime !== undefined) {
          const timeDiff = newTime - eventTime;
          swungEvent.startTime = newTime;
          if (event.endTime !== undefined) {
            swungEvent.endTime = event.endTime + timeDiff;
          }
        }
        
        // Note modified by swing timing
        // swungEvent.isSwung = true;
        // swungEvent.swingReason = 'moved-to-5/8';
        wasModified = true;
        
        // Note timing adjusted
      }
      
      // Rule 2: Adjust durations based on swing grid
      const startsOnBeat = Math.abs(beatFraction) < 0.05; // Within 5% of beat start
      const startsOffBeat = startsOnHalfBeat || Math.abs(beatFraction - 0.5) < 0.05;
      
      if (startsOnBeat || startsOffBeat) {
        let newDuration;
        
        if (startsOnBeat) {
          // On-beat: duration gets +5/8 adjustment
          const wholeDurationBeats = Math.floor(durationInBeats);
          const durationFraction = durationInBeats - wholeDurationBeats;
          newDuration = (wholeDurationBeats + 5/8) * beatDuration;
        } else {
          // Off-beat: duration gets +3/8 adjustment  
          const wholeDurationBeats = Math.floor(durationInBeats);
          const durationFraction = durationInBeats - wholeDurationBeats;
          newDuration = (wholeDurationBeats + 3/8) * beatDuration;
        }
        
        if (event.duration !== undefined) {
          swungEvent.duration = newDuration;
        } else if (event.startTime !== undefined && event.endTime !== undefined) {
          swungEvent.endTime = swungEvent.startTime + newDuration;
        }
        
        if (!wasModified) {
          // Duration modified by swing timing
          // swungEvent.isSwung = true;
          // swungEvent.swingReason = startsOnBeat ? 'duration-on-beat' : 'duration-off-beat';
        }
        wasModified = true;
        
        // Duration adjusted for swing grid
      }
      
      if (wasModified) {
        modifiedCount++;
      }
      
      return swungEvent;
    });
    
    // Swing modifications applied silently
    
    return results;
  }

  /**
   * Apply swing timing to loop events specifically
   * @param {Array} loopEvents - Array of loop events from loopUtils
   * @param {number} loopDuration - Duration of the loop
   * @param {number} beatsPerMeasure - Number of beats per measure
   * @param {number} beatDuration - Duration of one beat in seconds
   * @param {number} swingRatio - Swing ratio (default: 2/3)
   * @returns {Array} - Array of swing-adjusted loop events
   */
  static applySwingToLoopEvents(loopEvents, loopDuration, beatsPerMeasure = 4, beatDuration = 0.5, swingRatio = 5/8) {
    if (!loopEvents || loopEvents.length === 0) {
      return [];
    }

    // Calculate beat duration based on loop duration and measures
    const measuresInLoop = Math.ceil(loopDuration / (beatsPerMeasure * beatDuration));
    const actualBeatDuration = loopDuration / (measuresInLoop * beatsPerMeasure);

    return this.applySwingTiming(loopEvents, beatsPerMeasure, actualBeatDuration, swingRatio);
  }

  /**
   * Detect tempo and time signature from note events to calculate proper beat duration
   * @param {Array} noteEvents - Array of note events
   * @param {number} defaultBPM - Default BPM if detection fails
   * @returns {Object} - { beatDuration, beatsPerMeasure, estimatedBPM }
   */
  static detectTimingParameters(noteEvents, defaultBPM = 120) {
    if (!noteEvents || noteEvents.length < 2) {
      return {
        beatDuration: 60 / defaultBPM,
        beatsPerMeasure: 4,
        estimatedBPM: defaultBPM
      };
    }

    // Analyze note timing patterns to estimate beat duration
    const timeDiffs = [];
    const durations = [];
    
    for (let i = 1; i < Math.min(noteEvents.length, 50); i++) {
      const diff = noteEvents[i].time - noteEvents[i-1].time;
      if (diff > 0 && diff < 2.0) { // Reasonable range for beat divisions
        timeDiffs.push(diff);
      }
    }
    
    // Also look at note durations to help determine the beat
    for (let i = 0; i < Math.min(noteEvents.length, 50); i++) {
      const duration = noteEvents[i].duration;
      if (duration && duration > 0 && duration < 2.0) {
        durations.push(duration);
      }
    }

    if (timeDiffs.length === 0) {
      return {
        beatDuration: 60 / defaultBPM,
        beatsPerMeasure: 4,
        estimatedBPM: defaultBPM
      };
    }

    // Find the most common small time difference (likely eighth or quarter notes)
    timeDiffs.sort((a, b) => a - b);
    const timingMedian = timeDiffs[Math.floor(timeDiffs.length / 2)];
    
    durations.sort((a, b) => a - b);
    const durationMedian = durations.length > 0 ? durations[Math.floor(durations.length / 2)] : timingMedian;
    
    // Use both timing and duration to determine beat
    const smallestInterval = Math.min(timingMedian, durationMedian);
    
    // For swing detection, we need to identify the quarter note beat duration
    // If we see very small intervals (like 0.25s), these are likely eighth notes
    // So the quarter note beat would be double that
    let beatDuration;
    if (smallestInterval < 0.3) {
      // Likely eighth notes - beat duration should be double
      beatDuration = smallestInterval * 2;
    } else if (smallestInterval < 0.6) {
      // Likely quarter notes
      beatDuration = smallestInterval;
    } else {
      // Likely half notes or longer
      beatDuration = smallestInterval / 2;
    }

    const estimatedBPM = 60 / beatDuration;
    
    // console.log(`🎵 BEAT DETECTION: timingMedian=${timingMedian.toFixed(3)}s, durationMedian=${durationMedian.toFixed(3)}s, smallestInterval=${smallestInterval.toFixed(3)}s`);
    // console.log(`🎵 BEAT DETECTION: Final beatDuration=${beatDuration.toFixed(3)}s, estimatedBPM=${estimatedBPM.toFixed(1)}`);
    
    return {
      beatDuration,
      beatsPerMeasure: 4, // Default to 4/4 time
      estimatedBPM
    };
  }

  /**
   * Check if swing timing would significantly change the note events
   * @param {Array} originalEvents - Original note events
   * @param {Array} swungEvents - Swing-adjusted note events
   * @returns {boolean} - True if swing makes noticeable changes
   */
  static hasSignificantSwingChanges(originalEvents, swungEvents) {
    if (!originalEvents || !swungEvents || originalEvents.length !== swungEvents.length) {
      return false;
    }

    const threshold = 0.01; // 10ms threshold for "significant" change
    
    for (let i = 0; i < originalEvents.length; i++) {
      const timeDiff = Math.abs(originalEvents[i].time - swungEvents[i].time);
      const durationDiff = Math.abs(originalEvents[i].duration - swungEvents[i].duration);
      
      if (timeDiff > threshold || durationDiff > threshold) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Debug swing timing application
   * @param {Array} originalEvents - Original events
   * @param {Array} swungEvents - Swing-adjusted events
   * @param {number} maxExamples - Maximum number of examples to log
   */
  static debugSwingChanges(originalEvents, swungEvents, maxExamples = 5) {
    if (!originalEvents || !swungEvents) {
      // console.log('SwingUtils: No events to debug');
      return;
    }

    // console.log(`SwingUtils: Analyzing ${originalEvents.length} events for swing changes`);
    
    let changedCount = 0;
    let examplesShown = 0;
    
    for (let i = 0; i < originalEvents.length && examplesShown < maxExamples; i++) {
      const orig = originalEvents[i];
      const swung = swungEvents[i];
      
      const timeDiff = swung.time - orig.time;
      const durationDiff = swung.duration - orig.duration;
      
      if (Math.abs(timeDiff) > 0.001 || Math.abs(durationDiff) > 0.001) {
        changedCount++;
        
        if (examplesShown < maxExamples) {
          // console.log(`  Event ${i}: time ${orig.time.toFixed(3)} → ${swung.time.toFixed(3)} (${timeDiff >= 0 ? '+' : ''}${timeDiff.toFixed(3)}), duration ${orig.duration.toFixed(3)} → ${swung.duration.toFixed(3)} (${durationDiff >= 0 ? '+' : ''}${durationDiff.toFixed(3)})`);
          examplesShown++;
        }
      }
    }
    
    // console.log(`SwingUtils: ${changedCount} of ${originalEvents.length} events modified by swing`);
  }
}